/**
 * Live joke sources.
 *
 * Three public APIs, no keys, verified to send permissive CORS headers so a page on GitHub
 * Pages can call them directly. Two well-known alternatives — `quotable.io` and `zenquotes.io` —
 * were tested and rejected: both refuse cross-origin browser requests, so a static site cannot
 * use them at all.
 *
 * All three are **English only**, which is the whole reason the app ships a bundled Russian
 * corpus rather than relying on the network.
 *
 * Everything here races a short timeout and reports failure rather than throwing, because a
 * missing joke must never be the reason the app stops answering — the local corpus is always
 * there behind it.
 */

import type { Attribution } from '../core/types.js';

const TIMEOUT_MS = 3500;

export interface LiveLine {
  text: string;
  attribution: Attribution;
}

interface Source {
  id: string;
  url: string;
  /** Pulls the line out of whatever shape this particular API returns. */
  parse: (json: unknown) => LiveLine | undefined;
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

const SOURCES: Source[] = [
  {
    id: 'icanhazdadjoke',
    url: 'https://icanhazdadjoke.com/',
    parse: (json) => {
      const joke = str((json as { joke?: unknown }).joke);
      if (!joke) return undefined;
      return {
        text: joke,
        attribution: {
          author: 'icanhazdadjoke',
          licence: 'icanhazdadjoke.com',
          url: 'https://icanhazdadjoke.com/',
        },
      };
    },
  },
  {
    id: 'official-joke-api',
    url: 'https://official-joke-api.appspot.com/random_joke',
    parse: (json) => {
      const data = json as { setup?: unknown; punchline?: unknown };
      const setup = str(data.setup);
      const punchline = str(data.punchline);
      if (!setup || !punchline) return undefined;
      return {
        text: `${setup} — ${punchline}`,
        attribution: {
          author: 'Official Joke API',
          licence: 'official-joke-api.appspot.com',
          url: 'https://github.com/15Dkatz/official_joke_api',
        },
      };
    },
  },
  {
    id: 'dummyjson',
    url: 'https://dummyjson.com/quotes/random',
    parse: (json) => {
      const data = json as { quote?: unknown; author?: unknown };
      const quote = str(data.quote);
      if (!quote) return undefined;
      return {
        text: quote,
        attribution: {
          author: str(data.author) ?? 'Unknown',
          licence: 'dummyjson.com',
          url: 'https://dummyjson.com/docs/quotes',
        },
      };
    },
  },
];

export const SOURCE_COUNT = SOURCES.length;

async function fetchFrom(source: Source): Promise<LiveLine | undefined> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(source.url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return undefined;
    return source.parse(await response.json());
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetches one English line from a live source.
 *
 * @param randomValue a number in [0, 1) choosing which source to try first
 * @returns the line, or `undefined` when every source failed — never throws
 */
export async function fetchLiveLine(randomValue: number): Promise<LiveLine | undefined> {
  const clamped = Math.min(0.999999, Math.max(0, randomValue));
  const start = Math.floor(clamped * SOURCES.length);

  // Try each source once, starting from a different one each time so no single service
  // carries the whole demo and a single outage is invisible.
  for (let offset = 0; offset < SOURCES.length; offset += 1) {
    const source = SOURCES[(start + offset) % SOURCES.length];
    if (!source) continue;
    const line = await fetchFrom(source);
    if (line) return line;
  }
  return undefined;
}
